import { FunctionComponent } from 'react';
import { Table } from 'components/tables/Table';
import {
	Subscription,
	SubscriptionStatus,
} from 'domain/subscription/interfaces/Subscription';
import { RequestStatus } from 'types/RequestStatus';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FilterSettings } from 'components/tables/Table.interfaces';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { ExpandedBundledSubscriptionContent } from './ExpandedBundleSubscriptionContent/ExpandedBundleSubscriptionContent';
import { useNavigate } from 'react-router-dom';

const bundledColumnFilters: FilterSettings<Subscription> = {
	treatGroupsAsSingle: true,
	groups: [
		{
			columnId: 'status',
			filters: [
				{
					text: 'Active',
					value: [
						SubscriptionStatus.Active,
						SubscriptionStatus.Ordered,
					],
					selected: true,
				},
				{
					text: 'Inactive',
					value: [
						SubscriptionStatus.Expired,
						SubscriptionStatus.Canceled,
					],
					selected: false,
				},
				{
					text: 'All',
					value: undefined,
					selected: false,
				},
			],
		},
	],
};

const BundledSubscriptionsTable: FunctionComponent<{
	bundle: SubscriptionBundle;
	columns: ColumnDef<Subscription, any>[];
	requestStatus: RequestStatus;
	subscriptions: Subscription[];
	showRowCount?: boolean;
	expandable?: boolean;
	selectable?: boolean | ((row: Row<Subscription>) => boolean);
	onRowClicked?: (subscription: Subscription) => void;
	onUserUnassigned: () => void;
}> = ({
	bundle,
	columns,
	requestStatus,
	subscriptions,
	showRowCount = false,
	expandable = true,
	selectable = false,
	onRowClicked,
	onUserUnassigned,
}) => {
	const navigate = useNavigate();

	return (
		<Table<Subscription>
			columns={columns}
			containerGap="lg"
			padding="none"
			alternateRowBackground
			requestStatus={requestStatus}
			data={subscriptions}
			filterSettings={bundledColumnFilters}
			noDataText="No subscriptions in bundle"
			paginationOptions={{
				showFromCount: 10,
			}}
			withPagination
			showRowCount={showRowCount}
			withExpandableRows={expandable}
			expandableComponent={(subscription: Subscription) => (
				<ExpandedBundledSubscriptionContent
					subscription={subscription}
					productId={bundle.productId}
					onUserUnassigned={onUserUnassigned}
				/>
			)}
			onRowClicked={onRowClicked}
			withSelection={selectable}
			actionMenuItems={[
				{
					name: 'View',
					onClick: (subscription) =>
						navigate(`/subscriptions/${subscription.id}`),
				},
				{
					name: 'Edit',
					onClick: (subscription) =>
						navigate(`/subscriptions/${subscription.id}/edit`),
				},
			]}
		/>
	);
};

export default BundledSubscriptionsTable;
